import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import type { LatLngBoundsLiteral } from 'leaflet';
import { flyOptions } from '../lib/motion';
import { bboxTouchesMexico, insideMexico } from '../lib/mexico';
import { overlayPadding } from '../lib/mapPadding';

export interface FlyRequest {
  /** Token monotónicamente creciente para forzar re-fly aunque el target sea el mismo. */
  nonce: number;
  /** Cuando hay bbox lo preferimos por padding correcto. */
  bbox?: [number, number, number, number] | null;
  /** Fallback si no hay bbox: center + zoom directo. */
  center?: [number, number] | null;
  /** Zoom destino para centro o cap para bounds (estado=7, mun=11, col=14). */
  zoom: number;
}

interface Props {
  request: FlyRequest | null;
}

/**
 * Vive dentro de <MapContainer>. Cada vez que `request.nonce` cambia,
 * vuela el mapa al destino. Separado del FlyToOnFilters porque ese
 * está acoplado al cambio de filtros, mientras que esto responde a
 * eventos discretos (selección de un place del autocomplete).
 *
 * Manejar bbox y center por separado nos da suavidad: para una colonia
 * tipo "Polanco" con muchas properties usamos bbox + maxZoom=14;
 * para una colonia con 1-2 properties caemos a center + zoom=14 que
 * da un encuadre razonable sin "zoom-in extremo" del fitBounds.
 */
export function FlyToOnRequest({ request }: Props) {
  const map = useMap();

  useEffect(() => {
    if (!request) return;
    const motion = flyOptions(0.7);
    if (request.bbox && request.bbox.every(Number.isFinite)) {
      // Un destino fuera de México sólo puede venir de una coordenada
      // mala: se ignora en vez de mandar el mapa al océano.
      if (!bboxTouchesMexico(request.bbox)) return;
      const [lngMin, latMin, lngMax, latMax] = request.bbox;
      const bounds: LatLngBoundsLiteral = [[latMin, lngMin], [latMax, lngMax]];
      map.flyToBounds(bounds, {
        ...overlayPadding(map),
        maxZoom: request.zoom,
        ...motion,
      });
      return;
    }
    if (request.center && request.center.every(Number.isFinite)) {
      const [lng, lat] = request.center;
      if (!insideMexico(lng, lat)) return;
      map.flyTo([lat, lng], request.zoom, motion);
    }
  }, [request, map]);

  return null;
}
